import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { insightsAPI, teamsAPI } from '../services/api';
import toast from 'react-hot-toast';

function PlayerInsightsPage() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsData, recsData] = await Promise.all([
          insightsAPI.getPlayerStats(playerId),
          insightsAPI.getPlayerRecommendations(playerId)
        ]);
        setStats(statsData);
        setRecommendations(recsData);
      } catch (error) {
        console.error('Error loading player insights:', error);
        toast.error('Failed to load player data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [playerId]);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p>Loading player insights...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <h2>Player not found</h2>
          <button onClick={() => navigate(-1)} style={styles.backButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const actionLabels = {
    serve: '🎯 Serves',
    spike: '💥 Spikes',
    block: '🛡️ Blocks',
    dig: '🏊 Digs',
    set: '🙌 Sets',
    receive: '🤲 Receives',
    potential_action: '❓ Other Actions'
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backLink}>
          ← Back
        </button>
        <h1 style={styles.title}>
          #{stats.jersey_number} {stats.player_name}
        </h1>
        {stats.position && (
          <span style={styles.positionBadge}>{stats.position}</span>
        )}
      </div>

      {/* Stats Overview */}
      <div style={styles.overviewGrid}>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.total_matches}</span>
          <span style={styles.statLabel}>Matches</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.total_events}</span>
          <span style={styles.statLabel}>Total Actions</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>
            {stats.total_matches > 0 
              ? (stats.total_events / stats.total_matches).toFixed(1) 
              : 0}
          </span>
          <span style={styles.statLabel}>Actions/Match</span>
        </div>
      </div>

      {/* Action Breakdown */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>📊 Action Breakdown</h2>
        <div style={styles.card}>
          {Object.keys(stats.stats_by_type).length > 0 ? (
            <div style={styles.actionGrid}>
              {Object.entries(stats.stats_by_type).map(([action, count]) => (
                <div key={action} style={styles.actionItem}>
                  <span style={styles.actionLabel}>
                    {actionLabels[action] || action}
                  </span>
                  <div style={styles.actionBarContainer}>
                    <div 
                      style={{
                        ...styles.actionBar,
                        width: `${(count / stats.total_events) * 100}%`
                      }}
                    />
                  </div>
                  <span style={styles.actionCount}>{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.emptyText}>No actions recorded yet</p>
          )}
        </div>
      </div>

      {/* AI Recommendations */}
      {recommendations && (
        <>
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>💪 Strengths</h2>
            <div style={styles.card}>
              <ul style={styles.list}>
                {recommendations.strengths.map((strength, i) => (
                  <li key={i} style={styles.listItem}>
                    <span style={styles.checkIcon}>✓</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>🎯 Areas to Improve</h2>
            <div style={styles.card}>
              <ul style={styles.list}>
                {recommendations.areas_to_improve.map((area, i) => (
                  <li key={i} style={styles.listItem}>
                    <span style={styles.targetIcon}>◎</span>
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>🧠 Coaching Tips</h2>
            <div style={styles.card}>
              <ul style={styles.tipsList}>
                {recommendations.coaching_tips.map((tip, i) => (
                  <li key={i} style={styles.tipItem}>
                    <span style={styles.tipNumber}>{i + 1}</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>🏋️ Recommended Drills</h2>
            <div style={styles.drillsGrid}>
              {recommendations.practice_drills.map((drill, i) => (
                <div key={i} style={styles.drillCard}>
                  <span style={styles.drillIcon}>🔄</span>
                  <span>{drill}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.confidenceNote}>
            <span>🤖 AI Confidence: {(recommendations.confidence_score * 100).toFixed(0)}%</span>
            <span style={styles.generatedAt}>
              Generated: {new Date(recommendations.generated_at).toLocaleDateString()}
            </span>
          </div>
        </>
      )}

      {/* Recent Form */}
      {stats.recent_form.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📈 Recent Matches</h2>
          <div style={styles.recentMatchesGrid}>
            {stats.recent_form.map((match, i) => (
              <div key={i} style={styles.recentMatchCard}>
                <div style={styles.recentMatchHeader}>
                  <span style={styles.recentMatchTitle}>{match.title}</span>
                  <span style={styles.recentMatchDate}>
                    {new Date(match.date).toLocaleDateString()}
                  </span>
                </div>
                <div style={styles.recentMatchEvents}>
                  {Object.entries(match.events).map(([action, count]) => (
                    <span key={action} style={styles.eventTag}>
                      {action}: {count}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '32px 20px',
  },
  header: {
    marginBottom: '32px',
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#667eea',
    fontSize: '16px',
    cursor: 'pointer',
    marginBottom: '16px',
    display: 'block',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '8px',
  },
  positionBadge: {
    display: 'inline-block',
    padding: '6px 16px',
    background: '#e0e7ff',
    color: '#3730a3',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '32px',
  },
  statCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    color: 'white',
  },
  statNumber: {
    display: 'block',
    fontSize: '36px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  statLabel: {
    fontSize: '14px',
    opacity: 0.9,
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '16px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  actionGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionItem: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr 50px',
    alignItems: 'center',
    gap: '16px',
  },
  actionLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  actionBarContainer: {
    height: '8px',
    background: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  actionBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea, #764ba2)',
    borderRadius: '4px',
    transition: 'width 0.5s ease',
  },
  actionCount: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#667eea',
    textAlign: 'right',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '15px',
  },
  checkIcon: {
    color: '#10b981',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  targetIcon: {
    color: '#f59e0b',
    fontSize: '18px',
  },
  tipsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  tipItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 0',
    fontSize: '15px',
    lineHeight: '1.5',
  },
  tipNumber: {
    width: '24px',
    height: '24px',
    background: '#667eea',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  drillsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
  },
  drillCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: '#f0f9ff',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1e40af',
  },
  drillIcon: {
    fontSize: '20px',
  },
  confidenceNote: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    background: '#f9fafb',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '24px',
  },
  generatedAt: {
    fontStyle: 'italic',
  },
  recentMatchesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  recentMatchCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  recentMatchHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  recentMatchTitle: {
    fontWeight: '600',
    color: '#1f2937',
  },
  recentMatchDate: {
    fontSize: '13px',
    color: '#9ca3af',
  },
  recentMatchEvents: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  eventTag: {
    padding: '4px 10px',
    background: '#e0e7ff',
    color: '#3730a3',
    borderRadius: '12px',
    fontSize: '12px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    color: '#6b7280',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderTopColor: '#667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  errorCard: {
    textAlign: 'center',
    padding: '48px',
    background: 'white',
    borderRadius: '12px',
  },
  backButton: {
    marginTop: '16px',
    padding: '12px 24px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  emptyText: {
    color: '#9ca3af',
    textAlign: 'center',
    padding: '24px',
  },
};

export default PlayerInsightsPage;

